import classNames from 'classnames'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { InputText } from 'primereact/inputtext'
import { Password } from 'primereact/password'
import { Toast } from 'primereact/toast'
import React, { useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { auth } from '../../../App'
// @ts-ignore
import logo from '../../../assets/images/mokai-vertical.svg'

import { AuthRoutes } from '../../../router/routes'
import { getEmailRules } from '../../../util/validation/emailRules'
import { getCurrentVersion } from '../../../util/version'
import { LanguageSelect } from '../../util/button/language-select/LanguageSelect'
import { ResetPasswordDialog } from '../../util/dialog/ResetPasswordDialog'
import { LoadingSpinner } from '../../util/loading-spinner/LoadingSpinner'

export interface SignInData {
  email: string
  password: string
}

export const SignInForm = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [
    isPasswordForgottenDialogVisible,
    setIsPasswordForgottenDialogVisible,
  ] = useState<boolean>(false)
  const errorMessage = useRef<Toast>(null)

  // it is used for translation
  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  const { t } = useTranslation()

  const defaultValues = {
    email: '',
    password: '',
  }

  const {
    control,
    getValues,
    formState: { errors },
    setError,
    handleSubmit,
  } = useForm({ defaultValues })

  const navigate = useNavigate()

  const onSubmit = async (data: SignInData): Promise<void> => {
    errorMessage?.current?.clear()
    setIsLoading(true)
    let hasErrors = false
    // config session persistence: https://firebase.google.com/docs/auth/web/auth-state-persistence
    await signInWithEmailAndPassword(auth, data.email, data.password).catch(
      () => {
        hasErrors = true
        //showSignInFailedMessage()
        setError('email', { type: 'manual', message: '' })
        setError('password', {
          type: 'manual',
          message: t<string>('core.action.sign_in_failed.message'),
        })
        localStorage.removeItem('mokai-user')
      }
    )
    setIsLoading(false)
    if (!hasErrors) {
      navigate(AuthRoutes.dashboard)
    }
  }

  const getFormErrorMessage = (name: string) => {
    switch (name) {
      case 'email':
        return (
          errors.email && (
            <small className={'p-error'}>{errors.email.message}</small>
          )
        )
      case 'password':
        return (
          errors.password && (
            <small className={'p-error'}>{errors.password.message}</small>
          )
        )
      default:
        return (
          <small className={'p-error'}>
            {t<string>('core.form.default') +
              t<string>('core.form.required_text')}
          </small>
        )
    }
  }

  const getPasswordRules = () => {
    const requiredText =
      t<string>('person.password') + t<string>('core.form.required_text')
    return { required: requiredText }
  }

  const setPasswordResetDialogVisible = () => {
    setIsPasswordForgottenDialogVisible(true)
  }

  const hidePasswordResetDialog = () => {
    setIsPasswordForgottenDialogVisible(false)
  }

  return (
    <div
      className={
        'flex mt-6 justify-content-center mokai-gradient h-full sign-in '
      }
    >
      <Card className="col-12 mt-8 md:col-12 p-fluid sign-in-form-wrapper flex-wrap p-6">
        <div className={'w-full flex justify-content-center mb-6'}>
          <img
            alt={'Mokai'}
            width={'100px'}
            className={'sign-in-logo'}
            src={logo}
          />
        </div>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className={'w-full p-fluid flex-wrap p-0 '}>
            {/*<Button className={'p-button'} label={'Google'} onClick={onGoogleSubmit}/>*/}
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className={'formgrid grid pl-2'}>
                <div className="field col-12">
                  <label
                    htmlFor="email"
                    className={classNames({
                      'p-error': !!errors.email,
                    })}
                  >
                    {t<string>('person.email')}
                  </label>
                  <div>
                    <span className="p-input-icon-right">
                      <i className="fa-solid fa-envelope" />
                      <Controller
                        name="email"
                        control={control}
                        rules={getEmailRules()}
                        render={({ field, fieldState }) => (
                          <InputText
                            id={field.name}
                            {...field}
                            placeholder={'name@domain.ch'}
                            className={classNames({
                              'p-invalid': fieldState.error,
                            })}
                          />
                        )}
                      />
                    </span>
                    {getFormErrorMessage('email')}
                  </div>
                </div>
                <div className="field col-12">
                  <label
                    htmlFor="password"
                    className={classNames({
                      'p-error': errors.password,
                    })}
                  >
                    {t<string>('person.password')}
                  </label>
                  <div>
                    <Controller
                      name="password"
                      control={control}
                      rules={getPasswordRules()}
                      render={({ field, fieldState }) => (
                        <Password
                          id={field.name}
                          {...field}
                          toggleMask
                          autoComplete={'on'}
                          placeholder={'**************'}
                          className={classNames({
                            'p-invalid': fieldState.error,
                          })}
                          feedback={false}
                        />
                      )}
                    />
                    {getFormErrorMessage('password')}
                  </div>
                </div>
              </div>
              <div className="mt-2 mb-3">
                <Button
                  type="submit"
                  label={t<string>('core.sign_in.submit')}
                  className="p-button-raised"
                />
              </div>
            </form>
            <div className="mt-2 mb-2">
              <Button
                type="submit"
                className={'p-button-text p-button-raised'}
                label={t<string>('core.form.password_forgotten')}
                onClick={setPasswordResetDialogVisible}
              />
            </div>
            <ResetPasswordDialog
              visible={isPasswordForgottenDialogVisible}
              onClose={hidePasswordResetDialog}
              email={getValues('email')}
            />
            <p className={'secondary-text float-right mt-1 pr-1'}>
              {getCurrentVersion()}
            </p>
            <LanguageSelect />
          </div>
        )}
        <Toast className={' pt-2 mt-2'} ref={errorMessage} />
      </Card>
    </div>
  )
}